import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';

/**
 * FileDownloadService
 *
 * Centralizes file download operations avoiding direct DOM manipulation.
 * Uses Renderer2 for SSR compatibility and better Angular integration.
 *
 * Usage:
 * constructor(private fileDownloadService: FileDownloadService) {}
 *
 * this.fileDownloadService.downloadFile(url, filename);
 * this.fileDownloadService.downloadBlob(blob, filename);
 */
@Injectable({
  providedIn: 'root'
})
export class FileDownloadService {
  private renderer: Renderer2;

  constructor(
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    // Create renderer instance
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Downloads a file from a URL
   * @param url - The URL of the file to download
   * @param filename - The filename to save as (optional)
   * @param openInNewTab - Whether to open in new tab instead of download
   */
  downloadFile(url: string, filename?: string, openInNewTab: boolean = false): void {
    const link = this.renderer.createElement('a');
    this.renderer.setAttribute(link, 'href', url);

    if (openInNewTab) {
      this.renderer.setAttribute(link, 'target', '_blank');
    }

    if (filename) {
      this.renderer.setAttribute(link, 'download', filename);
    }

    this.renderer.appendChild(this.document.body, link);
    link.click();
    this.renderer.removeChild(this.document.body, link);
  }

  /**
   * Downloads a Blob as a file
   * @param blob - The Blob to download
   * @param filename - The filename to save as
   */
  downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    this.downloadFile(url, filename);

    // Cleanup object URL after a short delay
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  /**
   * Downloads data as JSON file
   * @param data - The data to download as JSON
   * @param filename - The filename (without .json extension)
   */
  downloadJson(data: any, filename: string): void {
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    this.downloadBlob(blob, `${filename}.json`);
  }

  /**
   * Downloads text content as a file
   * @param content - The text content to download
   * @param filename - The filename
   * @param mimeType - The MIME type (default: text/plain)
   */
  downloadText(content: string, filename: string, mimeType: string = 'text/plain'): void {
    const blob = new Blob([content], { type: mimeType });
    this.downloadBlob(blob, filename);
  }

  /**
   * Opens a PDF in a new tab (for preview)
   * @param url - The URL of the PDF
   */
  openPdfInNewTab(url: string): void {
    this.downloadFile(url, undefined, true);
  }

  /**
   * Downloads a file from a data URL (base64)
   * @param dataUrl - The data URL (e.g., data:image/png;base64,...)
   * @param filename - The filename to save as
   */
  downloadDataUrl(dataUrl: string, filename: string): void {
    this.downloadFile(dataUrl, filename);
  }
}
